import { useTheme } from "@mui/material";
import { Clock, type LucideIcon, PiggyBank } from "lucide-react";
import { useMemo } from "react";
import { useFormatGoalAmount } from "web_myplann/Goals/Helpers/formatGoalAmount";
import type { CurrencyEnum } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

export type WhatIfScenario = "earlier" | "more";

export type WhatIfScenarioInfo = {
	label: string;
	subtitle: string;
	accent: string;
	icon: LucideIcon;
};

type UseWhatIfScenariosParams = {
	canFinishEarlier: boolean;
	savedLabel: string;
	extraTotal: number;
	currencyCode?: CurrencyEnum;
};

type UseWhatIfScenariosResult = {
	scenarios: WhatIfScenario[];
	scenarioInfo: Record<WhatIfScenario, WhatIfScenarioInfo>;
};

const WhatIfResources = Resources.Goals.Detail.WhatIf;

export const useWhatIfScenarios = ({
	canFinishEarlier,
	savedLabel,
	extraTotal,
	currencyCode,
}: UseWhatIfScenariosParams): UseWhatIfScenariosResult => {
	const { palette } = useTheme();
	const { t } = useResource();
	const formatAmount = useFormatGoalAmount();

	return useMemo(
		() => ({
			scenarios: canFinishEarlier ? ["earlier", "more"] : ["more"],
			scenarioInfo: {
				earlier: {
					label: t(WhatIfResources.Earlier.label),
					subtitle: t(WhatIfResources.Earlier.subtitle, { saved: savedLabel }),
					accent: palette.success.main,
					icon: Clock,
				},
				more: {
					label: t(WhatIfResources.More.label),
					subtitle: t(WhatIfResources.More.subtitle, {
						amount: formatAmount(extraTotal, currencyCode),
					}),
					accent: palette.blue.main,
					icon: PiggyBank,
				},
			},
		}),
		[
			canFinishEarlier,
			savedLabel,
			extraTotal,
			currencyCode,
			t,
			palette,
			formatAmount,
		],
	);
};
